import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppVersionStore } from '../store/useAppVersionStore';
import { useToastStore } from '../store/useToastStore';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
/** Fokuswechsel kommen in Schüben — Prüfungen daraus werden gedrosselt. */
const FOCUS_THROTTLE_MS = 60 * 1000;

/**
 * Prüft periodisch, ob der Server ein neueres Client-Bundle ausliefert, und meldet
 * das einmalig als bleibenden Toast mit Reload-Button.
 *
 * Einmalig in App.tsx eingehängt. In Dev inaktiv — der Vite-Dev-Server erzeugt keine
 * version.json.
 */
export function useVersionPolling(): void {
  const { t } = useTranslation();
  const updateAvailable = useAppVersionStore((s) => s.updateAvailable);
  const notified = useAppVersionStore((s) => s.notified);
  const lastFocusCheckRef = useRef(0);

  useEffect(() => {
    if (import.meta.env.DEV) return;
    if (updateAvailable) return;

    const { check } = useAppVersionStore.getState();

    const checkThrottled = () => {
      const now = Date.now();
      if (now - lastFocusCheckRef.current < FOCUS_THROTTLE_MS) return;
      lastFocusCheckRef.current = now;
      void check();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkThrottled();
    };

    const interval = window.setInterval(() => void check(), POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', checkThrottled);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', checkThrottled);
    };
  }, [updateAvailable]);

  useEffect(() => {
    if (!updateAvailable || notified) return;

    useAppVersionStore.getState().markNotified();
    useToastStore.getState().addToast(t('releaseNotes:update.message'), 'info', {
      // duration 0 → der Toast bleibt stehen, bis der Nutzer neu lädt oder ihn schließt.
      duration: 0,
      action: {
        label: t('releaseNotes:update.reload'),
        onClick: () => window.location.reload(),
      },
    });
  }, [updateAvailable, notified, t]);
}

export default useVersionPolling;
